import { useState } from 'react';
import { showToast, toastApiError } from '@doccareplus/kernel/application';
import { Button, Card, SearchInput, Textarea } from '@doccareplus/ui';
import {
  useClinicBookingOptionsQuery,
  usePortalClinicsQuery,
  useUnlinkedAppointmentRequestsQuery,
} from '../application/appointments.query';
import { useRequestClinicAppointmentMutation } from '../application/appointments.command';
import type { PortalClinicDto } from '../infrastructure/appointments.api';
import { ClinicRequestsListSkeleton, ClinicResultsGridSkeleton, SlotOptionsSkeleton } from './ClinicDiscoverySkeletons';

function clinicLocation(clinic: PortalClinicDto) {
  return [clinic.city, clinic.stateCode, clinic.countryCode].filter(Boolean).join(', ');
}

function slotLabel(startTime: string, timezone: string) {
  return new Date(startTime).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
}

export function ClinicDiscoveryPage() {
  const [search, setSearch] = useState('');
  const [clinic, setClinic] = useState<PortalClinicDto | null>(null);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const clinics = usePortalClinicsQuery(search);
  const previousRequests = useUnlinkedAppointmentRequestsQuery();
  const options = useClinicBookingOptionsQuery(clinic?.clinicId ?? null, providerId, serviceId);
  const request = useRequestClinicAppointmentMutation();

  const selectClinic = (nextClinic: PortalClinicDto) => {
    setClinic(nextClinic);
    setProviderId(null);
    setServiceId(null);
    setStartTime(null);
  };

  const submit = () => {
    if (!clinic || !providerId || !startTime) return;
    request.mutate(
      {
        clinicId: clinic.clinicId,
        body: { startTime, providerId, serviceId, reason: reason.trim() || null },
      },
      {
        onSuccess: () => {
          showToast({ type: 'success', message: 'Appointment request sent to the clinic.' });
          setStartTime(null);
          setReason('');
        },
        onError: (error) => toastApiError(error, 'Could not send the appointment request.'),
      }
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h1 className="text-[24px] font-bold tracking-[-0.01em]">Find care</h1>
        <p className="mt-1 text-[13px] leading-5 text-[var(--text-2)]">
          Request a clinic appointment even before your patient record is connected.
        </p>
      </header>

      {previousRequests.isLoading ? (
        <ClinicRequestsListSkeleton label="Loading your open clinic requests" />
      ) : previousRequests.isError ? (
        <Card role="alert" className="p-4">
          <p className="text-[13px] text-[var(--danger)]">Your recent requests could not be loaded.</p>
          <Button className="mt-3" size="sm" variant="outline" onClick={() => void previousRequests.refetch()}>
            Try again
          </Button>
        </Card>
      ) : previousRequests.data?.requests.length ? (
        <section>
          <h2 className="mb-3 text-[15px] font-bold">Your open clinic requests</h2>
          <div className="flex flex-col gap-2">
            {previousRequests.data.requests.map(({ clinicName, timezone, appointment }) => (
              <Card key={appointment.id} className="p-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[13.5px] font-semibold">{clinicName}</div>
                    <div className="mt-0.5 text-[12px] text-[var(--text-2)]">
                      {new Date(appointment.startTime).toLocaleString([], {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                        timeZone: timezone,
                      })}
                    </div>
                  </div>
                  <span className="rounded-full bg-[var(--warning-bg)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--warning)]">
                    {appointment.status}
                  </span>
                </div>
                {(appointment.reason ?? appointment.patientNote) && (
                  <p className="mt-2 text-[12.5px] text-[var(--text-2)]">
                    {appointment.reason ?? appointment.patientNote}
                  </p>
                )}
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      <section className="flex flex-col gap-3">
        <SearchInput
          label="Search clinics"
          placeholder="Clinic name or city"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onClear={() => setSearch('')}
        />
        {clinics.isLoading ? (
          <ClinicResultsGridSkeleton label="Loading clinics" />
        ) : clinics.isError ? (
          <Card role="alert" className="p-4">
            <p className="text-[13px] text-[var(--danger)]">Clinics could not be loaded.</p>
            <Button className="mt-3" size="sm" variant="outline" onClick={() => void clinics.refetch()}>
              Try again
            </Button>
          </Card>
        ) : clinics.data?.clinics.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {clinics.data.clinics.map((item) => (
              <Card key={item.clinicId} className={`p-4 ${clinic?.clinicId === item.clinicId ? 'border-[var(--primary)]' : ''}`}>
                <div className="text-[15px] font-bold">{item.name}</div>
                {clinicLocation(item) && <div className="mt-1 text-[12.5px] text-[var(--text-2)]">{clinicLocation(item)}</div>}
                {item.type && <div className="mt-0.5 text-[11.5px] text-[var(--text-3)]">{item.type}</div>}
                <Button
                  className="mt-4"
                  size="sm"
                  variant={clinic?.clinicId === item.clinicId ? 'primary' : 'outline'}
                  aria-label={`View available times at ${item.name}`}
                  onClick={() => selectClinic(item)}
                >
                  View available times
                </Button>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-5 text-center">
            <p className="text-[14px] font-semibold">No clinics found</p>
            <p className="mt-1 text-[12.5px] text-[var(--text-3)]">Try a clinic name, city, or a broader search.</p>
          </Card>
        )}
      </section>

      {clinic && (
        <section className="flex flex-col gap-4 border-t border-[var(--border)] pt-5">
          <div>
            <h2 className="text-[17px] font-bold">{clinic.name}</h2>
            <p className="text-[12.5px] text-[var(--text-2)]">Choose a provider and an available time.</p>
          </div>
          {options.isLoading ? (
            <SlotOptionsSkeleton label="Loading available times" />
          ) : options.isError ? (
            <Card role="alert" className="p-4">
              <p className="text-[13px] text-[var(--danger)]">Available times could not be loaded.</p>
              <Button className="mt-3" size="sm" variant="outline" onClick={() => void options.refetch()}>
                Try again
              </Button>
            </Card>
          ) : options.data ? (
            <>
              <div>
                <h3 className="mb-2 text-[12.5px] font-semibold text-[var(--text-2)]">Provider</h3>
                <div className="flex flex-wrap gap-2">
                  {options.data.providers.map((provider) => (
                    <Button
                      key={provider.id}
                      size="sm"
                      variant={providerId === provider.id ? 'primary' : 'outline'}
                      onClick={() => {
                        setProviderId(provider.id);
                        setStartTime(null);
                      }}
                    >
                      {provider.name}
                    </Button>
                  ))}
                </div>
              </div>

              {options.data.services.length > 0 && (
                <div>
                  <h3 className="mb-2 text-[12.5px] font-semibold text-[var(--text-2)]">Service</h3>
                  <div className="flex flex-wrap gap-2">
                    {options.data.services.map((service) => (
                      <Button
                        key={service.id}
                        size="sm"
                        variant={serviceId === service.id ? 'primary' : 'outline'}
                        onClick={() => {
                          setServiceId(service.id);
                          setStartTime(null);
                        }}
                      >
                        {service.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {providerId && (
                <div>
                  <h3 className="mb-2 text-[12.5px] font-semibold text-[var(--text-2)]">Available times</h3>
                  {options.data.days.some((day) => day.slots.length > 0) ? (
                    <div className="flex flex-col gap-3">
                      {options.data.days.filter((day) => day.slots.length > 0).map((day) => (
                        <div key={day.date}>
                          <div className="mb-2 text-[12px] font-semibold text-[var(--text-3)]">
                            {new Date(`${day.date}T12:00:00`).toLocaleDateString([], {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {day.slots.map((slot) => (
                              <Button
                                key={slot.startTime}
                                size="sm"
                                variant={startTime === slot.startTime ? 'primary' : 'outline'}
                                onClick={() => setStartTime(slot.startTime)}
                              >
                                {slotLabel(slot.startTime, options.data.timezone)}
                              </Button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[13px] text-[var(--text-3)]">No available times for this provider.</p>
                  )}
                </div>
              )}

              <Textarea
                label="Reason for visit"
                optional
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="A short note for the clinic"
              />
              <Button
                fullWidth
                disabled={!providerId || !startTime || request.isPending || options.isFetching}
                isLoading={request.isPending}
                onClick={submit}
              >
                Send appointment request
              </Button>
            </>
          ) : null}
        </section>
      )}
    </div>
  );
}
